/**
 * ConsultationRequestForm — the single, canonical VidaVital consultation
 * form. Rendered by both `/request-consultation` and `/medical-consultation`
 * (Google Ads landing page) so both surfaces share one validated code path.
 *
 * Behavior:
 *   • Captures UTMs, referrer, landing URL, GCLID on mount.
 *   • Validates client-side (name ≥2 chars, valid email, phone ≥7 digits, consent).
 *   • Focuses the first errored field on submit for accessibility.
 *   • POSTs /leads with form_type="Consultation Request".
 *   • On success: stashConfirmation → window.location.replace("/thank-you").
 *
 * Props:
 *   - sourceLabel: string — analytics `location` label for the submit event.
 *     Defaults to `form.pathname`. Also used as the `data-source` attribute
 *     on the <form> so we can filter downstream.
 *   - formName: string — the form_name we persist with the lead so GTM can
 *     distinguish primary vs. SEM landing traffic.
 *   - showEmergencyBlurb: bool — the /request-consultation page shows a
 *     "For emergencies, call 911" line in its consent copy. The SEM page
 *     compresses this into the disclaimer under the form.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import api from "../lib/api";
import Turnstile, { readTurnstileToken } from "./Turnstile";
import { getAttribution, stashConfirmation, captureAttribution } from "../lib/attribution";

const CLINIC_LOCATIONS = [
  { value: "",           label: "Choose a clinic…" },
  { value: "Nevada",     label: "Nevada — Las Vegas" },
  { value: "New Mexico", label: "New Mexico — Albuquerque" },
  { value: "Florida",    label: "Florida — Fort Lauderdale" },
  { value: "Wisconsin",  label: "Wisconsin — Milwaukee" },
  { value: "Nebraska",   label: "Nebraska — Omaha" },
  { value: "Telehealth", label: "Telehealth (where legally appropriate)" },
  { value: "Not sure",   label: "Not sure yet" },
];

const SERVICES_OF_INTEREST = [
  { value: "",                       label: "Choose a service…" },
  { value: "Not sure yet",           label: "Not sure yet — I'd like guidance" },
  { value: "Weight Management",      label: "Weight Management (Semaglutide / Tirzepatide)" },
  { value: "Hormone Optimization",   label: "Hormone Optimization" },
  { value: "Sexual Health",          label: "Sexual Health (PT-141, Kisspeptin)" },
  { value: "Recovery & Healing",     label: "Recovery & Healing (BPC-157, TB-500)" },
  { value: "Longevity & Anti-Aging", label: "Longevity & Anti-Aging (NAD+, Epitalon)" },
  { value: "Cognitive Support",      label: "Cognitive Support (Semax, Selank)" },
  { value: "Immune Support",         label: "Immune Support (Thymosin Alpha-1)" },
  { value: "Growth Hormone Peptides", label: "Growth Hormone Peptides (Sermorelin, CJC-1295)" },
  { value: "Aesthetics",             label: "Aesthetics (GHK-Cu, Glutathione)" },
  { value: "Other",                  label: "Something else" },
];

const CONTACT_METHODS = [
  { value: "Email", label: "Email" },
  { value: "Phone", label: "Phone" },
  { value: "Text",  label: "Text" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ConsultationRequestForm({
  formName = "consultation_request_form",
  sourceLabel = "",
  showEmergencyBlurb = true,
  submitLabel = "Request Consultation",
  className = "",
  testIdPrefix = "rc",
}) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_contact_method: "Email",
    preferred_clinic: "",
    service_interest: "",
    goals: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  useEffect(() => { captureAttribution(); }, []);

  const set = (k, v) => {
    setForm((cur) => ({ ...cur, [k]: v }));
    if (errors[k]) setErrors((cur) => { const n = { ...cur }; delete n[k]; return n; });
  };

  const validate = () => {
    const next = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) next.full_name = "Please enter your full name.";
    if (!form.email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) next.phone = "Enter a valid phone number.";
    if (!form.consent) next.consent = "You must agree before submitting.";
    return next;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      const first = Object.keys(found)[0];
      const el = document.querySelector(`[data-testid="${testIdPrefix}-${first}"]`);
      if (el && typeof el.focus === "function") el.focus();
      return;
    }
    setSubmitting(true);
    try {
      const attribution = getAttribution();
      const parts = form.full_name.trim().split(/\s+/);
      const firstName = parts[0];
      const lastName = parts.slice(1).join(" ");
      const payload = {
        form_type: "Consultation Request",
        first_name: firstName,
        last_name: lastName,
        email: form.email.trim(),
        phone: form.phone.trim(),
        preferred_location: form.preferred_clinic,
        treatment_interest: form.service_interest ? [form.service_interest] : [],
        message: form.goals.trim(),
        preferred_contact_method: form.preferred_contact_method,
        consent_to_contact: true,
        source_page: sourceLabel || attribution.landing_url || window.location.pathname,
        referrer: attribution.referrer_url || document.referrer || "",
        landing_url: attribution.landing_url || "",
        utm_source: attribution.utm_source || "",
        utm_medium: attribution.utm_medium || "",
        utm_campaign: attribution.utm_campaign || "",
        utm_content: attribution.utm_content || "",
        utm_term: attribution.utm_term || "",
        gclid: attribution.gclid || "",
        gbraid: attribution.gbraid || "",
        wbraid: attribution.wbraid || "",
        form_name: formName,
        device: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
        website: "",
        cf_turnstile_response:
          turnstileToken ||
          readTurnstileToken(document.querySelector(`[data-testid="${testIdPrefix}-form"]`)) ||
          "",
      };
      const { data } = await api.post("/leads", payload);
      if (!data || !data.confirmation) throw new Error("No confirmation returned");
      stashConfirmation({ confirmation: data.confirmation, formName });
      window.location.replace("/thank-you");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const clinicOptions = useMemo(() => CLINIC_LOCATIONS, []);
  const serviceOptions = useMemo(() => SERVICES_OF_INTEREST, []);

  const inputCls = (name) =>
    `mt-2 w-full border-b-2 bg-transparent px-1 py-2 text-base text-stone-900 focus:outline-none focus:border-[color:var(--vv-gold-deep)] ${
      errors[name] ? "border-rose-400" : "border-stone-300"
    }`;
  const selectCls = inputCls;

  const tid = (name) => `${testIdPrefix}-${name}`;

  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-form`}
      data-form-name={formName}
      data-source={sourceLabel}
      noValidate
      className={`bg-white rounded-2xl border border-stone-200 shadow-sm p-6 md:p-10 ${className}`}
    >
      <div className="grid md:grid-cols-2 gap-6">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Full Name <span className="text-rose-500">*</span></span>
          <input
            type="text"
            autoComplete="name"
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            className={inputCls("full_name")}
            data-testid={tid("full_name")}
            aria-invalid={!!errors.full_name}
            aria-describedby={errors.full_name ? `${tid("full_name")}-err` : undefined}
          />
          {errors.full_name ? <div id={`${tid("full_name")}-err`} className="mt-1 text-xs text-rose-600">{errors.full_name}</div> : null}
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Email <span className="text-rose-500">*</span></span>
          <input
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className={inputCls("email")}
            data-testid={tid("email")}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? `${tid("email")}-err` : undefined}
          />
          {errors.email ? <div id={`${tid("email")}-err`} className="mt-1 text-xs text-rose-600">{errors.email}</div> : null}
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Phone <span className="text-rose-500">*</span></span>
          <input
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            className={inputCls("phone")}
            data-testid={tid("phone")}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? `${tid("phone")}-err` : undefined}
          />
          {errors.phone ? <div id={`${tid("phone")}-err`} className="mt-1 text-xs text-rose-600">{errors.phone}</div> : null}
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Preferred Contact Method</span>
          <select
            value={form.preferred_contact_method}
            onChange={(e) => set("preferred_contact_method", e.target.value)}
            className={selectCls("preferred_contact_method")}
            data-testid={tid("preferred_contact_method")}
          >
            {CONTACT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Preferred Clinic</span>
          <select
            value={form.preferred_clinic}
            onChange={(e) => set("preferred_clinic", e.target.value)}
            className={selectCls("preferred_clinic")}
            data-testid={tid("preferred_clinic")}
          >
            {clinicOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Service of Interest</span>
          <select
            value={form.service_interest}
            onChange={(e) => set("service_interest", e.target.value)}
            className={selectCls("service_interest")}
            data-testid={tid("service_interest")}
          >
            {serviceOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>

        <label className="block md:col-span-2">
          <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Treatment Goals or Questions <span className="text-stone-400 normal-case tracking-normal font-normal">(optional)</span></span>
          <textarea
            rows={4}
            value={form.goals}
            onChange={(e) => set("goals", e.target.value)}
            className={`mt-2 w-full border-b-2 bg-transparent px-1 py-2 text-base text-stone-900 focus:outline-none focus:border-[color:var(--vv-gold-deep)] resize-none ${errors.goals ? "border-rose-400" : "border-stone-300"}`}
            data-testid={tid("goals")}
            placeholder="Share what you'd like to discuss. Please do not include medical history, medications, or protected health information."
          />
        </label>
      </div>

      <div className="mt-6 flex items-start gap-3">
        <input
          id={tid("consent")}
          type="checkbox"
          checked={form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-stone-300 text-[color:var(--vv-gold-deep)] focus:ring-[color:var(--vv-gold-deep)]"
          data-testid={tid("consent")}
          aria-invalid={!!errors.consent}
          aria-describedby={errors.consent ? `${tid("consent")}-err` : undefined}
        />
        <label htmlFor={tid("consent")} className="text-sm text-stone-700 leading-relaxed">
          I understand this form does not create a provider-patient relationship, and I consent to be contacted by VidaVital Medical about my request. Please do not submit medical history, medications, or protected health information here.
          {showEmergencyBlurb ? " For emergencies, call 911." : ""}
        </label>
      </div>
      {errors.consent ? <div id={`${tid("consent")}-err`} className="mt-1 text-xs text-rose-600 ml-7">{errors.consent}</div> : null}

      <div className="mt-6">
        <Turnstile onToken={setTurnstileToken} />
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2 text-xs text-stone-500">
          <ShieldCheck size={14} className="text-[color:var(--vv-teal)]" />
          Secure. We never share your information.
        </div>
        <button
          type="submit"
          disabled={submitting}
          aria-busy={submitting}
          data-testid={tid("submit")}
          className="inline-flex items-center justify-center rounded-full bg-[color:var(--vv-gold-deep)] text-white font-semibold px-8 py-3.5 text-sm uppercase tracking-widest hover:opacity-95 disabled:opacity-60 transition"
        >
          {submitting ? "Submitting…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

export default ConsultationRequestForm;
